import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';

import { apiClient } from '@/api/client';
import { mapPatientSummaries } from '@/api/mappers';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { AddFamilyMemberBody } from '@/features/families/api';
import type { PatientSummaryViewModel } from '@/models/patient';

import { MemberCandidatePicker } from './MemberCandidatePicker';

interface AddMemberLinkExistingPanelProps {
  readonly busy: boolean;
  readonly excludePersonGuids: ReadonlySet<string>;
  readonly onCancel: () => void;
  readonly onSubmit: (body: AddFamilyMemberBody) => void;
}

const DEBOUNCE_MS = 300;
const MAX_CANDIDATES = 8;
const MIN_QUERY_LEN = 2;

/**
 * "Link existing patient" mode of the AddMemberDialog: debounced
 * `/persons/search`, keyboard-navigable candidate picker. Submits the
 * linked person's GUID — the BE attaches them to this family.
 */
export function AddMemberLinkExistingPanel({
  busy,
  excludePersonGuids,
  onCancel,
  onSubmit,
}: AddMemberLinkExistingPanelProps): ReactElement {
  const { t } = useTranslation();
  const [lastName, setLastName] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selectedGuid, setSelectedGuid] = useState<string | null>(null);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebounced(lastName.trim());
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(handle);
    };
  }, [lastName]);

  const canSearch = debounced.length >= MIN_QUERY_LEN;

  const search = useQuery<readonly PatientSummaryViewModel[]>({
    queryKey: ['family-member-link-search', debounced],
    queryFn: async ({ signal }) => {
      const page = await apiClient.searchPersons(
        { last_name: debounced, size: MAX_CANDIDATES },
        { signal },
      );
      return mapPatientSummaries(page.content);
    },
    enabled: canSearch,
    staleTime: 30_000,
  });

  const candidates = useMemo<readonly PatientSummaryViewModel[]>(
    () =>
      (search.data ?? []).filter((c) => !excludePersonGuids.has(c.guid)),
    [search.data, excludePersonGuids],
  );

  // Drop the selection if the active search no longer surfaces it.
  useEffect(() => {
    if (selectedGuid !== null && !candidates.some((c) => c.guid === selectedGuid)) {
      setSelectedGuid(null);
    }
  }, [candidates, selectedGuid]);

  const canSubmit = selectedGuid !== null && !busy;

  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (selectedGuid === null || busy) return;
        const body: AddFamilyMemberBody = {
          cascade_role: 'FAMILY_MEMBER',
          person_id: selectedGuid,
        };
        onSubmit(body);
      }}
    >
      <div className="space-y-1">
        <Label htmlFor="link-existing-last-name">
          {t('routes.family.members.addDialog.linkExisting.lastName')}
        </Label>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            id="link-existing-last-name"
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
            }}
            placeholder={t('routes.family.members.addDialog.linkExisting.lastNamePlaceholder')}
            autoComplete="off"
            className="pl-8"
          />
        </div>
      </div>

      <MemberCandidatePicker
        candidates={candidates}
        selectedGuid={selectedGuid}
        onSelect={setSelectedGuid}
        loading={search.isFetching && canSearch}
        isError={search.isError}
        hasQuery={canSearch}
        showMinLengthHint={!canSearch && lastName.trim().length > 0}
        minQueryLen={MIN_QUERY_LEN}
        candidatesAriaLabel={t('routes.family.members.addDialog.linkExisting.candidatesAria')}
      />

      <DialogFooter className="pt-2">
        <Button variant="ghost" type="button" onClick={onCancel} disabled={busy}>
          {t('ui.dialog.close')}
        </Button>
        <Button
          type="submit"
          disabled={!canSubmit}
          loading={busy}
          loadingText={t('patient.dirtyFooter.saving')}
        >
          {t('routes.family.members.addDialog.linkExisting.submit')}
        </Button>
      </DialogFooter>
    </form>
  );
}
